import React, { memo, useEffect, useCallback } from 'react';
import { CloseButton } from './CloseButton';

/**
 * Modal - A consistent dialog/overlay component
 * 
 * @example
 * <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="פרטי מדבקה">
 *   <p>תוכן המודאל</p>
 * </Modal>
 */

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title (optional) */
  title?: string;
  /** Modal content */
  children: React.ReactNode;
  /** Size preset */
  size?: ModalSize;
  /** Whether clicking the backdrop closes the modal */
  closeOnBackdrop?: boolean;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Additional CSS classes for the modal content */
  className?: string;
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-xs sm:max-w-sm',
  md: 'max-w-xs sm:max-w-md',
  lg: 'max-w-sm sm:max-w-lg md:max-w-xl',
};

export const Modal: React.FC<ModalProps> = memo(({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdrop = true,
  showCloseButton = true,
  className = '',
}) => {
  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    if (closeOnBackdrop) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className={`
          bg-white dark:bg-dark-card
          rounded-2xl sm:rounded-3xl
          p-4 sm:p-6 md:p-8
          m-3 sm:m-4
          w-full
          animate-pop
          shadow-2xl
          relative
          transition-colors duration-300
          ${sizeStyles[size]}
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        {showCloseButton && (
          <CloseButton
            onClick={onClose}
            position="absolute"
            size="medium"
          />
        )}
        
        {/* Title */}
        {title && (
          <h2 className="text-title text-center text-gray-800 dark:text-dark-text mb-4 sm:mb-6 pr-8">
            {title}
          </h2>
        )}
        
        {/* Content */}
        {children}
      </div>
    </div>
  );
});

Modal.displayName = 'Modal';
